import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ProCard } from '@ant-design/pro-components';
import { App, Button, Col, Drawer, Row, Space, Spin, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import Permission, { useAccess } from '@/components/permission';
import {
    THEATER_BANNER_CONFIG_KEY,
    THEATER_BANNER_CONFIG_MODULE,
    THEATER_BANNER_MAX_ITEMS,
    THEATER_BANNER_PERMISSION,
    THEATER_BANNER_UPDATE_PERMISSION,
} from '@/constants/theater-banner';
import BannerList from '@/pages/client-banner/components/BannerList';
import DramaPicker from '@/pages/client-banner/components/DramaPicker';
import GlobalSettings from '@/pages/client-banner/components/GlobalSettings';
import { fetchDramasByIds, useDramaCatalog } from '@/pages/client-banner/hooks/useDramaCatalog';
import { useBannerConfig } from '@/pages/client-banner/hooks/useBannerConfig';
import {
    addBannerItem,
    buildBannerConfigValue,
    canAddBannerItem,
    isBannerDramaUnavailable,
    mergeEditorItemsWithCatalog,
    removeBannerItem,
    resolveBannerEnabled,
} from '@/pages/client-banner/utils';
import { isAbortError, RequestGuard } from '@/utils/request-guard';

import type { DramaItem } from '@/services/dramas';

const CONFIG_PERMISSION = THEATER_BANNER_PERMISSION;
const CONFIG_UPDATE_PERMISSION = THEATER_BANNER_UPDATE_PERMISSION;

const PageClientBanner: React.FC = () => {
    const { message } = App.useApp();
    const { formatMessage } = useIntl();
    const access = useAccess();

    const {
        dramas,
        loading: catalogLoading,
        total: catalogTotal,
        page,
        pageSize,
        pageSizeOptions,
        keyword,
        onlineOnly,
        sortField,
        sortDirection,
        bootstrap,
        refreshAll,
        handlePageChange,
        handleKeywordChange,
        handleOnlineOnlyChange,
        handleSortFieldChange,
        handleSortDirectionChange,
    } = useDramaCatalog();
    const {
        loading: configLoading,
        saving,
        settings,
        setSettings,
        items,
        setItems,
        loadConfig,
        save,
    } = useBannerConfig();

    const canEditConfig = access(CONFIG_UPDATE_PERMISSION);
    const canMutate = canEditConfig;
    const readOnly = !canEditConfig;

    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedResolved, setSelectedResolved] = useState(false);
    const [resolvedDramas, setResolvedDramas] = useState<DramaItem[]>([]);
    const autoSavingRef = useRef(false);
    const selectedResolveGuard = useMemo(() => new RequestGuard(), []);
    /** 同一批已选 ID 只发一次校验；避免 effect 重跑把进行中的 id 查询 abort 成「已取消」 */
    const resolvingIdsKeyRef = useRef<string | null>(null);
    const resolvedIdsKeyRef = useRef<string | null>(null);

    const resolveSelectedDramas = useCallback(
        async (dramaIds: string[]) => {
            const idsKey = dramaIds.join(',');
            if (resolvedIdsKeyRef.current === idsKey) {
                setSelectedResolved(true);
                return;
            }
            if (resolvingIdsKeyRef.current === idsKey) {
                return;
            }

            const { id, signal } = selectedResolveGuard.begin();
            resolvingIdsKeyRef.current = idsKey;

            if (!dramaIds.length) {
                setResolvedDramas([]);
                setSelectedResolved(true);
                resolvedIdsKeyRef.current = idsKey;
                resolvingIdsKeyRef.current = null;
                return;
            }

            setSelectedResolved(false);
            try {
                const map = await fetchDramasByIds(dramaIds, signal);
                if (!selectedResolveGuard.isCurrent(id)) return;
                setResolvedDramas(Array.from(map.values()));
                setSelectedResolved(true);
                resolvedIdsKeyRef.current = idsKey;
            } catch (error) {
                if (isAbortError(error)) return;
                if (!selectedResolveGuard.isCurrent(id)) return;
                setSelectedResolved(false);
                resolvedIdsKeyRef.current = null;
                throw error;
            } finally {
                if (selectedResolveGuard.isCurrent(id)) {
                    resolvingIdsKeyRef.current = null;
                }
            }
        },
        [selectedResolveGuard],
    );

    useEffect(() => {
        const init = async () => {
            try {
                await Promise.all([loadConfig(), bootstrap({ initial: true })]);
            } catch (error: any) {
                message.error(error?.message || formatMessage({ id: 'clientBanner.load.failed' }));
            }
        };

        void init();
        // 仅首屏加载配置与剧集库；排序变更由 useDramaCatalog 内部重新拉取
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedIdsKey = useMemo(() => items.map((item) => item.dramaId).join(','), [items]);

    /** 配置加载后：按已选 ID（≤10）逐条校验，兼容已删/下架/列表不可见 */
    useEffect(() => {
        if (configLoading) {
            // 重新拉配置时作废缓存，避免同 ID 集合跳过复查
            resolvedIdsKeyRef.current = null;
            resolvingIdsKeyRef.current = null;
            setSelectedResolved(false);
            return;
        }
        const ids = selectedIdsKey ? selectedIdsKey.split(',') : [];
        void resolveSelectedDramas(ids).catch((error: any) => {
            message.error(error?.message || formatMessage({ id: 'clientBanner.load.failed' }));
        });
    }, [configLoading, formatMessage, message, resolveSelectedDramas, selectedIdsKey]);

    /** 用「已选 ID 校验结果」合并编辑态；校验完成后才会 prune */
    useEffect(() => {
        setItems((prev) => {
            if (!prev.length && !resolvedDramas.length) return prev;
            const next = mergeEditorItemsWithCatalog(prev, resolvedDramas, { selectedResolved });
            if (
                next.length === prev.length &&
                next.every((item, index) => {
                    const old = prev[index];
                    return (
                        old &&
                        item.dramaId === old.dramaId &&
                        item.missing === old.missing &&
                        item.drama === old.drama &&
                        item.thumbUrl === old.thumbUrl
                    );
                })
            ) {
                return prev;
            }
            return next;
        });
    }, [resolvedDramas, selectedResolved, setItems]);

    /** 当前页命中已选项时，顺带刷新预览字段（不触发 prune） */
    useEffect(() => {
        if (!dramas.length) return;
        setItems((prev) => {
            if (!prev.length) return prev;
            const selectedOnPage = dramas.filter((drama) => prev.some((item) => item.dramaId === drama.id));
            if (!selectedOnPage.length) return prev;
            const next = mergeEditorItemsWithCatalog(prev, selectedOnPage, { selectedResolved: false });
            if (
                next.length === prev.length &&
                next.every((item, index) => {
                    const old = prev[index];
                    return (
                        old &&
                        item.dramaId === old.dramaId &&
                        item.drama === old.drama &&
                        item.thumbUrl === old.thumbUrl
                    );
                })
            ) {
                return prev;
            }
            return next;
        });
    }, [dramas, setItems]);

    /** 检测到 DELETED / OFFLINE / 列表不可见后，自动保存配置 */
    useEffect(() => {
        if (!canMutate || configLoading || saving || autoSavingRef.current || !selectedResolved) return;
        if (!items.length) return;

        const merged = mergeEditorItemsWithCatalog(items, resolvedDramas, { selectedResolved: true });
        const removedIds = items
            .filter((item) => !merged.some((entry) => entry.dramaId === item.dramaId))
            .map((item) => item.dramaId);

        if (!removedIds.length) return;

        autoSavingRef.current = true;
        void (async () => {
            setItems(merged);
            try {
                const nextSettings = {
                    enabled: resolveBannerEnabled(merged.length, settings.enabled),
                };
                setSettings(nextSettings);
                const result = await save(merged, nextSettings);
                if (result.conflict) {
                    message.warning(formatMessage({ id: 'clientBanner.save.conflict' }));
                    await refreshAll();
                    await loadConfig();
                    return;
                }
                message.success(
                    formatMessage({ id: 'clientBanner.save.removedDeleted' }, { count: removedIds.length }),
                );
            } catch (error: any) {
                message.error(error?.message || formatMessage({ id: 'clientBanner.save.failed' }));
                await loadConfig();
            } finally {
                autoSavingRef.current = false;
            }
        })();
    }, [
        canMutate,
        configLoading,
        formatMessage,
        items,
        loadConfig,
        message,
        refreshAll,
        resolvedDramas,
        save,
        saving,
        selectedResolved,
        setItems,
        setSettings,
        settings,
    ]);

    const selectedIds = useMemo(() => new Set(items.map((item) => item.dramaId)), [items]);

    /** 无剧集时强制关总开关（不允许空 Banner 开着） */
    useEffect(() => {
        if (items.length === 0 && settings.enabled) {
            setSettings({ enabled: false });
        }
    }, [items.length, setSettings, settings.enabled]);

    const previewJson = useMemo(
        () => JSON.stringify(buildBannerConfigValue(items, settings), null, 2),
        [items, settings],
    );

    const handleToggleDrama = useCallback(
        (drama: Parameters<typeof addBannerItem>[1], checked: boolean) => {
            if (readOnly) return;

            if (checked) {
                if (isBannerDramaUnavailable(drama)) {
                    message.warning(formatMessage({ id: 'clientBanner.picker.unavailable' }));
                    return;
                }
                if (!canAddBannerItem(items)) {
                    message.warning(
                        formatMessage({ id: 'clientBanner.picker.maxReached' }, { max: THEATER_BANNER_MAX_ITEMS }),
                    );
                    return;
                }
                // 手动勾选后同步进已校验集合，避免紧接着的按 ID 复查把它当「不可用」清掉
                resolvedIdsKeyRef.current = null;
                // 从 0 → 1：默认打开总开关，之后可再手动关掉
                if (!items.length) {
                    setSettings({ enabled: true });
                }
                setItems((prev) => addBannerItem(prev, drama));
                setResolvedDramas((prev) => {
                    if (prev.some((item) => item.id === drama.id)) return prev;
                    return [...prev, drama];
                });
                return;
            }
            resolvedIdsKeyRef.current = null;
            setItems((prev) => removeBannerItem(prev, drama.id));
            setResolvedDramas((prev) => prev.filter((item) => item.id !== drama.id));
        },
        [formatMessage, items, message, readOnly, setItems, setSettings],
    );

    const handleSave = useCallback(async () => {
        if (!canMutate) return;

        try {
            const itemsToSave = selectedResolved
                ? mergeEditorItemsWithCatalog(items, resolvedDramas, { selectedResolved: true })
                : items;
            if (itemsToSave.length !== items.length) {
                setItems(itemsToSave);
            }
            const nextSettings = {
                enabled: resolveBannerEnabled(itemsToSave.length, settings.enabled),
            };
            setSettings(nextSettings);
            const result = await save(itemsToSave, nextSettings);
            if (result.conflict) {
                message.warning(formatMessage({ id: 'clientBanner.save.conflict' }));
                await refreshAll();
                await loadConfig();
                return;
            }
            message.success(formatMessage({ id: 'clientBanner.save.success' }));
        } catch (error: any) {
            message.error(error?.message || formatMessage({ id: 'clientBanner.save.failed' }));
        }
    }, [
        canMutate,
        formatMessage,
        items,
        loadConfig,
        message,
        refreshAll,
        resolvedDramas,
        save,
        selectedResolved,
        setItems,
        setSettings,
        settings,
    ]);

    return (
        <Permission permission={CONFIG_PERMISSION}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                    <div>
                        <Typography.Title level={4} style={{ marginBottom: 4 }}>
                            <FormattedMessage id="clientBanner.title" />
                        </Typography.Title>
                        <Typography.Text type="secondary">
                            <FormattedMessage
                                id="clientBanner.subtitle"
                                values={{
                                    key: THEATER_BANNER_CONFIG_KEY,
                                    module: THEATER_BANNER_CONFIG_MODULE,
                                }}
                            />
                        </Typography.Text>
                    </div>
                    <Space>
                        <Button onClick={() => setPreviewOpen(true)}>
                            <FormattedMessage id="clientBanner.previewJson" />
                        </Button>
                        {canMutate && (
                            <Button type="primary" loading={saving} onClick={handleSave}>
                                <FormattedMessage id="b.save" />
                            </Button>
                        )}
                    </Space>
                </Space>

                {readOnly && (
                    <Typography.Text type="secondary">
                        <FormattedMessage id="clientBanner.readOnly" />
                    </Typography.Text>
                )}

                <ProCard>
                    <GlobalSettings
                        disabled={readOnly}
                        hasItems={items.length > 0}
                        settings={settings}
                        onChange={setSettings}
                    />
                </ProCard>

                <Row gutter={16}>
                    <Col xs={24} lg={12}>
                        <ProCard
                            title={<FormattedMessage id="clientBanner.picker.panelTitle" />}
                            extra={
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    <FormattedMessage
                                        id="clientBanner.picker.loaded"
                                        values={{ total: catalogTotal }}
                                    />
                                </Typography.Text>
                            }
                            bordered
                        >
                            <DramaPicker
                                dramas={dramas}
                                selectedIds={selectedIds}
                                loading={catalogLoading}
                                disabled={readOnly}
                                maxItems={THEATER_BANNER_MAX_ITEMS}
                                selectedCount={items.length}
                                total={catalogTotal}
                                page={page}
                                pageSize={pageSize}
                                pageSizeOptions={pageSizeOptions}
                                keyword={keyword}
                                onlineOnly={onlineOnly}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onPageChange={(nextPage, nextPageSize) => {
                                    void handlePageChange(nextPage, nextPageSize).catch((error: any) => {
                                        message.error(
                                            error?.message || formatMessage({ id: 'clientBanner.load.failed' }),
                                        );
                                    });
                                }}
                                onKeywordChange={(nextKeyword) => {
                                    void handleKeywordChange(nextKeyword).catch((error: any) => {
                                        message.error(
                                            error?.message || formatMessage({ id: 'clientBanner.load.failed' }),
                                        );
                                    });
                                }}
                                onOnlineOnlyChange={(checked) => {
                                    void handleOnlineOnlyChange(checked).catch((error: any) => {
                                        message.error(
                                            error?.message || formatMessage({ id: 'clientBanner.load.failed' }),
                                        );
                                    });
                                }}
                                onSortFieldChange={handleSortFieldChange}
                                onSortDirectionChange={handleSortDirectionChange}
                                onToggle={handleToggleDrama}
                            />
                        </ProCard>
                    </Col>
                    <Col xs={24} lg={12}>
                        <ProCard
                            title={<FormattedMessage id="clientBanner.list.title" />}
                            extra={
                                <Typography.Text type="secondary">
                                    <FormattedMessage
                                        id="clientBanner.list.count"
                                        values={{ count: items.length, max: THEATER_BANNER_MAX_ITEMS }}
                                    />
                                </Typography.Text>
                            }
                            bordered
                        >
                            <Spin spinning={configLoading}>
                                <BannerList disabled={readOnly} items={items} onChange={setItems} />
                            </Spin>
                        </ProCard>
                    </Col>
                </Row>
            </Space>

            <Drawer
                title={<FormattedMessage id="clientBanner.previewJson" />}
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                width={640}
            >
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{previewJson}</pre>
            </Drawer>
        </Permission>
    );
};

export default PageClientBanner;
